import { useEffect, useState } from "react";
import { CheckCircle2, CreditCard, Loader2, Repeat, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PaymentChoice } from "@/components/PaymentChoice";
import { GiftCodeField, remiseCadeau } from "@/components/GiftCodeField";
import { api, formatCHF } from "@/lib/api";

const inputCls = "w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra";
const PRIX = 400;
const MENSUALITE = 67;
const MENSUALITES = 6;

const FORMULES = [
  { id: "total", label: "En une fois", detail: `${formatCHF(PRIX)} — TWINT, carte, facture ou carte cadeau`, icon: CreditCard },
  { id: "mensuel", label: `${MENSUALITES} × ${formatCHF(MENSUALITE)} / mois`, detail: "Prélèvement automatique par carte, arrêt après la 6e mensualité", icon: Repeat },
];

export const WyrdBooking = () => {
  const [cycle, setCycle] = useState(null);
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", message: "", paiement: "en_ligne", mode_paiement: "total" });
  const [carte, setCarte] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState(null);

  const refresh = () => api.events({ type: "parcours" }).then((e) => setCycle(e.find((x) => new Date(x.date_fin || x.date) > new Date()) || null)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));
  const mensuel = form.mode_paiement === "mensuel";
  const remise = mensuel ? 0 : remiseCadeau(carte, PRIX);
  const total = PRIX - remise;
  const complet = cycle && cycle.places_restantes <= 0;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.book({ type: "wyrd", ...form, paiement: mensuel ? "en_ligne" : form.paiement, participants: 1, origin_url: window.location.origin, code_cadeau: mensuel ? null : carte?.code || null });
      if (res.checkout_url) { window.location.assign(res.checkout_url); return; }
      setConfirmation(res);
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue, merci de réessayer.");
      refresh();
    } finally {
      setLoading(false);
    }
  };

  if (confirmation) {
    return (
      <Reveal>
        <div className="bg-sage-light/50 border border-sage/30 rounded-2xl p-10 text-center" data-testid="wyrd-success">
          <CheckCircle2 className="w-12 h-12 text-sage mx-auto mb-4" />
          <p className="font-serif text-3xl text-bark mb-3">Votre place dans la Toile est réservée</p>
          <p className="text-fumee max-w-md mx-auto">
            Référence <span className="font-mono text-terra">{confirmation.reference}</span> — {confirmation.event_titre} · {formatCHF(confirmation.montant)} ·{" "}
            {confirmation.paiement === "carte_cadeau" ? "réglé par carte cadeau" : "règlement par facture sous 10 jours"}. Un courriel de confirmation arrive vers {confirmation.email}.
          </p>
        </div>
      </Reveal>
    );
  }

  return (
    <Reveal>
      <form onSubmit={submit} className="bg-card border border-sand-dark rounded-2xl p-6 lg:p-8 space-y-5" data-testid="wyrd-booking">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <p className="font-serif text-2xl text-bark">Rejoindre le parcours</p>
          {cycle && (
            <span className={`font-mono text-xs tracking-wider inline-flex items-center gap-1.5 ${complet ? "text-destructive" : "text-terra"}`} data-testid="wyrd-places">
              <Users className="w-3.5 h-3.5" />{complet ? "Complet" : `${cycle.places_restantes} place${cycle.places_restantes > 1 ? "s" : ""} sur ${cycle.places_total}`}
            </span>
          )}
        </div>
        {cycle && <p className="text-sm text-fumee -mt-2" data-testid="wyrd-cycle">{cycle.titre} · {cycle.date_label}</p>}
        <div>
          <span className="eyebrow block mb-3">Formule de paiement</span>
          <div className="grid sm:grid-cols-2 gap-2 min-w-0" role="radiogroup" aria-label="Formule de paiement">
            {FORMULES.map(({ id, label, detail, icon: Icon }) => {
              const active = form.mode_paiement === id;
              return (
                <button type="button" key={id} data-testid={`wyrd-formule-${id}`} onClick={() => setForm((f) => ({ ...f, mode_paiement: id }))}
                  className={`flex items-start gap-3 text-left rounded-xl border px-3 py-3 transition-colors duration-300 ${active ? "border-terra bg-terra/5" : "border-wood/20 hover:border-wood/40"}`}>
                  <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${active ? "text-terra" : "text-fumee"}`} />
                  <span className="min-w-0">
                    <span className={`block text-sm font-medium ${active ? "text-terra" : "text-bark"}`}>{label}</span>
                    <span className="block text-[11px] text-fumee mt-0.5 leading-snug">{detail}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="wyrd-nom" className="eyebrow block mb-2">Nom complet</label>
            <input id="wyrd-nom" data-testid="wyrd-nom" required value={form.nom} onChange={set("nom")} className={inputCls} />
          </div>
          <div>
            <label htmlFor="wyrd-email" className="eyebrow block mb-2">Courriel</label>
            <input id="wyrd-email" data-testid="wyrd-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
          </div>
        </div>
        <div>
          <label htmlFor="wyrd-tel" className="eyebrow block mb-2">Téléphone (pour le groupe WhatsApp)</label>
          <input id="wyrd-tel" data-testid="wyrd-telephone" type="tel" required minLength={6} value={form.telephone} onChange={set("telephone")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="wyrd-msg" className="eyebrow block mb-2">Un mot sur votre appel (optionnel)</label>
          <textarea id="wyrd-msg" data-testid="wyrd-message" rows={3} value={form.message} onChange={set("message")} placeholder="Débutant·e ou déjà en chemin, ce qui vous amène au tambour…" className={`${inputCls} resize-none`} />
        </div>
        {!mensuel && (
          <>
            <div>
              <span className="eyebrow block mb-2">Paiement</span>
              <PaymentChoice compact value={form.paiement} onChange={(v) => setForm((f) => ({ ...f, paiement: v }))} testidPrefix="wyrd" />
            </div>
            <GiftCodeField carte={carte} onChange={setCarte} testidPrefix="wyrd" />
          </>
        )}
        {error && <p data-testid="wyrd-error" className="text-sm text-destructive">{error}</p>}
        <div className="flex items-center justify-between pt-2 border-t border-sand-dark">
          <div>
            <span className="font-serif text-xl text-bark" data-testid="wyrd-total">{mensuel ? `${formatCHF(MENSUALITE)} / mois` : formatCHF(total)}</span>
            <p className="text-[11px] text-fumee mt-1">{mensuel ? `${MENSUALITES} mensualités · total ${formatCHF(PRIX)} · 1re prélevée aujourd'hui` : remise > 0 ? `carte cadeau : − ${formatCHF(remise)}` : "parcours complet de 6 mois"}</p>
          </div>
          <button type="submit" data-testid="wyrd-submit" disabled={loading || complet || !cycle}
            className="px-7 py-3 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-earth transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Un instant…" : complet ? "Complet" : mensuel || (form.paiement === "en_ligne" && total > 0) ? "M'inscrire et payer (TWINT / carte)" : "M'inscrire"}
          </button>
        </div>
      </form>
    </Reveal>
  );
};
